import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { certificatesApi } from '../utils/api';

type CuratedSummary = {
  key: string;
  title: string;
  summary: string;
  authority?: string;
  defaultPrefix: string;
  version: number;
  groupCount: number;
  fieldCount: number;
};

type PendingTemplate = {
  id: string;
  certificatePrefix: string;
  certificateTypeName: string;
  templateLabel: string;
  createdAt: string;
  reviewStatus: string;
  source?: string;
  curatedKey?: string | null;
};

export default function CertificateTemplatesAdmin() {
  const qc = useQueryClient();
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedCurated, setSelectedCurated] = useState<Set<string>>(new Set());

  const curatedQuery = useQuery({
    queryKey: ['certs', 'curated-library'],
    queryFn: async () => {
      const r = await certificatesApi.listCuratedLibrary();
      return (r.data?.data || r.data) as CuratedSummary[];
    },
  });

  const pendingQuery = useQuery({
    queryKey: ['certs', 'pending-review'],
    queryFn: async () => {
      const r = await certificatesApi.listTemplatesPendingReview();
      return (r.data?.data || r.data) as PendingTemplate[];
    },
  });

  const seedMut = useMutation({
    mutationFn: (keys: string[] | undefined) =>
      certificatesApi.seedCurated({ keys, overwrite: false }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['certs'] });
      setSelectedCurated(new Set());
    },
  });

  const approveMut = useMutation({
    mutationFn: (id: string) => certificatesApi.approveTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['certs'] }),
  });

  const rejectMut = useMutation({
    mutationFn: (args: { id: string; notes: string }) =>
      certificatesApi.rejectTemplate(args.id, args.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['certs'] });
      setRejectId(null);
      setRejectReason('');
    },
  });

  const toggleCurated = (key: string) => {
    setSelectedCurated((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-10">
      <div>
        <Link to="/dashboard/settings" className="text-sm text-slate-500 hover:text-slate-700">
          ← Back to Settings
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Certificate templates</h1>
        <p className="mt-1 text-sm text-slate-600">
          Add Gas Safe curated templates to your workspace, or review custom uploads before engineers can use them.
        </p>
      </div>

      {/* ---------------- Curated library ---------------- */}
      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Curated Gas Safe library</h2>
            <p className="text-sm text-slate-500 mt-1">
              Pre-built, compliance-reviewed templates. Highly recommended over custom uploads for standard certificates.
            </p>
          </div>
          <button
            onClick={() =>
              seedMut.mutate(
                selectedCurated.size > 0 ? Array.from(selectedCurated) : undefined,
              )
            }
            disabled={seedMut.isPending || curatedQuery.isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-lg text-sm"
          >
            {seedMut.isPending
              ? 'Seeding…'
              : selectedCurated.size > 0
                ? `Add ${selectedCurated.size} to my workspace`
                : 'Add all to my workspace'}
          </button>
        </div>

        {seedMut.isSuccess && (
          <div className="mt-4 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2">
            Added {(seedMut.data as any)?.data?.data?.created?.length || 0} templates
            {' · '}updated {(seedMut.data as any)?.data?.data?.updated?.length || 0}
            {' · '}already existed {(seedMut.data as any)?.data?.data?.skipped?.length || 0}.
          </div>
        )}

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {curatedQuery.isLoading && <div className="text-sm text-slate-500">Loading library…</div>}
          {curatedQuery.data?.map((item) => {
            const checked = selectedCurated.has(item.key);
            return (
              <label
                key={item.key}
                className={`flex gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                  checked ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCurated(item.key)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-slate-900 text-white">
                      {item.key}
                    </span>
                    <span className="text-sm font-semibold text-slate-900">{item.title}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{item.summary}</p>
                  <div className="mt-2 text-[11px] text-slate-400">
                    {item.groupCount} sections · {item.fieldCount} fields · prefix {item.defaultPrefix} · v{item.version}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      {/* ---------------- Pending custom review ---------------- */}
      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Custom templates awaiting review
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              New custom PDF templates stay in review until an admin approves them. Engineers cannot fill in templates that haven't been reviewed.
            </p>
          </div>
          <Link
            to="/dashboard/settings/certificate-templates/new"
            className="text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            Upload new template →
          </Link>
        </div>

        <div className="mt-4 divide-y divide-slate-100">
          {pendingQuery.isLoading && <div className="text-sm text-slate-500 py-2">Loading…</div>}
          {pendingQuery.data && pendingQuery.data.length === 0 && !pendingQuery.isLoading && (
            <div className="text-sm text-slate-500 py-6 text-center">
              Nothing awaiting review. New uploads will appear here.
            </div>
          )}
          {pendingQuery.data?.map((t) => (
            <div key={t.id} className="py-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">
                    PENDING
                  </span>
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {t.templateLabel}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {t.certificateTypeName} · prefix {t.certificatePrefix} · uploaded{' '}
                  {new Date(t.createdAt).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => approveMut.mutate(t.id)}
                disabled={approveMut.isPending}
                className="text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md disabled:opacity-60"
              >
                Approve
              </button>
              <button
                onClick={() => {
                  setRejectId(t.id);
                  setRejectReason('');
                }}
                className="text-sm font-semibold border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md"
              >
                Reject
              </button>
            </div>
          ))}
        </div>
      </section>

      {rejectId && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-slate-900">Reject template?</h3>
            <p className="text-sm text-slate-600 mt-1">
              The template will be blocked from use. Tell the team why.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="mt-3 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. Fields are mis-grouped — please split the tightness test section"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setRejectId(null);
                  setRejectReason('');
                }}
                className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700"
              >
                Cancel
              </button>
              <button
                disabled={rejectReason.trim().length < 5 || rejectMut.isPending}
                onClick={() => rejectId && rejectMut.mutate({ id: rejectId, notes: rejectReason.trim() })}
                className="text-sm px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-60"
              >
                Reject template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
